import { createUseStyles } from 'react-jss';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { Spacings } from '../../../../foundation/spacings/spacings/index.js';
import { SecondaryColors } from '../../../../foundation/colors/secondaryColors/secondaryColors/index.js';
import { hideScrollbars } from '../../../../foundation/styleHelpers/hideScrollbars/hideScrollbars/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';
import { ControlBorderColors } from '../../../../foundation/colors/controlBorderColors/controlBorderColors/index.js';
import { ControlTextColors } from '../../../../foundation/colors/controlTextColors/controlTextColors/index.js';
import { FontWeights } from '../../../../foundation/fonts/fonts/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';

const line = {
    content: '""',
    position: 'absolute',
    bottom: Spacings.s0,
    left: Spacings.s0,
    width: 'var(--active-tab-width)',
    height: 2,
    transform: 'translate(var(--active-tab-x), 0)',
    transition: `all ${Timings.default} ${Easing.linear}`,
    pointerEvents: 'none',
};
const useTabsStyles = createUseStyles({
    root: {
        '--tabs-height': ({ sizes }) => sizes.Default.value?.height && `${sizes.Default.value?.height}px`,
        '--active-tab-x': ({ activeTabUI }) => `${activeTabUI?.x}px`,
        '--active-tab-width': ({ activeTabUI }) => `${activeTabUI?.width}px`,
        display: 'grid',
        minHeight: 'var(--tabs-height)',
        maxHeight: 'var(--tabs-height)',
        overflow: 'auto hidden',
        scrollBehavior: 'smooth',
        overscrollBehavior: 'contain',
        justifyContent: ({ isFixed }) => !isFixed && 'start',
        ...hideScrollbars,
        [BreakPointsMedia.XXL]: {
            '--tabs-height': ({ sizes }) => sizes.XXL.value?.height && `${sizes.XXL.value?.height}px`,
        },
        [BreakPointsMedia.XL]: {
            '--tabs-height': ({ sizes }) => sizes.XL.value?.height && `${sizes.XL.value?.height}px`,
        },
        [BreakPointsMedia.LG]: {
            '--tabs-height': ({ sizes }) => sizes.LG.value?.height && `${sizes.LG.value?.height}px`,
        },
        [BreakPointsMedia.MD]: {
            '--tabs-height': ({ sizes }) => sizes.MD.value?.height && `${sizes.MD.value?.height}px`,
        },
        [BreakPointsMedia.SM]: {
            '--tabs-height': ({ sizes }) => sizes.SM.value?.height && `${sizes.SM.value?.height}px`,
        },
        [BreakPointsMedia.XS]: {
            '--tabs-height': ({ sizes }) => sizes.XS.value?.height && `${sizes.XS.value?.height}px`,
        },
        [BreakPointsMedia.XXS]: {
            '--tabs-height': ({ sizes }) => sizes.XXS.value?.height && `${sizes.XXS.value?.height}px`,
        },
    },
    tabList: {
        '--active-tab-indicator-color': SecondaryColors.base.active,
        position: 'relative',
        display: 'flex',
        height: 'var(--tabs-height)',
        '&[active-tab-hovered]': {
            '--active-tab-indicator-color': SecondaryColors.base.activeAction,
        },
        '&::after': {
            ...line,
            backgroundColor: 'var(--active-tab-indicator-color)',
        },
    },
}, {
    name: 'tabs',
});
const useTabStyles = createUseStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        columnGap: Spacings.s4,
        position: 'relative',
        backgroundColor: 'transparent',
        color: ControlTextColors.default,
        height: '100%',
        fontWeight: FontWeights.semibold,
        transitionProperty: 'color',
        transitionTimingFunction: Easing.ease,
        transitionDuration: Timings.default,
        border: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flex: ({ isFixed }) => !isFixed && 'none',
        width: ({ isFixed }) => isFixed && '100%',
        fontSize: ({ sizes }) => sizes.Default.value?.fontSize,
        lineHeight: ({ sizes }) => sizes.Default.value?.lineHeight,
        padding: ({ sizes }) => sizes.Default.value?.paddingHorizontal,
        [BreakPointsMedia.XXL]: {
            fontSize: ({ sizes }) => sizes.XXL.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XXL.value?.lineHeight,
            padding: ({ sizes }) => sizes.XXL.value?.paddingHorizontal,
        },
        [BreakPointsMedia.XL]: {
            fontSize: ({ sizes }) => sizes.XL.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XL.value?.lineHeight,
            padding: ({ sizes }) => sizes.XL.value?.paddingHorizontal,
        },
        [BreakPointsMedia.LG]: {
            fontSize: ({ sizes }) => sizes.LG.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.LG.value?.lineHeight,
            padding: ({ sizes }) => sizes.LG.value?.paddingHorizontal,
        },
        [BreakPointsMedia.MD]: {
            fontSize: ({ sizes }) => sizes.MD.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.MD.value?.lineHeight,
            padding: ({ sizes }) => sizes.MD.value?.paddingHorizontal,
        },
        [BreakPointsMedia.SM]: {
            fontSize: ({ sizes }) => sizes.SM.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.SM.value?.lineHeight,
            padding: ({ sizes }) => sizes.SM.value?.paddingHorizontal,
        },
        [BreakPointsMedia.XS]: {
            fontSize: ({ sizes }) => sizes.XS.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XS.value?.lineHeight,
            padding: ({ sizes }) => sizes.XS.value?.paddingHorizontal,
        },
        [BreakPointsMedia.XXS]: {
            fontSize: ({ sizes }) => sizes.XXS.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XXS.value?.lineHeight,
            padding: ({ sizes }) => sizes.XXS.value?.paddingHorizontal,
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: Spacings.s0,
            left: Spacings.s0,
            right: Spacings.s0,
            height: 2,
            opacity: 0,
            transition: `all ${Timings.default} ${Easing.linear}`,
            backgroundColor: 'transparent',
            pointerEvents: 'none',
        },
    },
    interactive: {
        [canHover]: {
            '&:hover': {
                color: ControlTextColors.action,
                '&::after': {
                    opacity: 1,
                    backgroundColor: ControlBorderColors.action,
                },
            },
        },
        [canTouch]: {
            '&:active': {
                color: ControlTextColors.action,
                '&::after': {
                    opacity: 1,
                    backgroundColor: ControlBorderColors.action,
                },
            },
        },
    },
    active: {
        color: SecondaryColors.base.active,
        [canHover]: {
            '&:hover': {
                color: SecondaryColors.base.activeAction,
            },
        },
        [canTouch]: {
            '&:active': {
                color: SecondaryColors.base.activeAction,
            },
        },
    },
    icon: {
        width: 24,
        height: 24,
        '& path': {
            fill: 'currentcolor',
        },
    },
}, {
    name: 'tab',
});

export { useTabStyles, useTabsStyles };
